class WhatsAppWidget {
  constructor(element = null) {
    this.widget = element || document.querySelector('.whatsapp-widget');
    if (!this.widget) {
      console.error('WhatsApp Widget: No widget element found, aborting initialization');
      return;
    }
    
    this.container = this.widget.closest('.whatsapp-widget-container');
    this.panel = this.container ? this.container.querySelector('.whatsapp-widget-panel') : null;
    this.panelClose = this.panel ? this.panel.querySelector('.whatsapp-widget-panel__close') : null;
    this.panelButtons = this.panel ? this.panel.querySelectorAll('.whatsapp-widget-panel__btn') : [];
    
    this.phoneNumber = this.getPhoneNumber();
    this.customMessage = this.getCustomMessage();
    this.init();
  }

  getPhoneNumber() {
    if (this.widget && this.widget.dataset.phone) {
      return this.widget.dataset.phone;
    }
    return '972545146427'; // Fallback to correct WhatsApp format
  }

  getCustomMessage() {
    if (this.widget && this.widget.dataset.message) {
      return this.widget.dataset.message;
    }
    return '';
  }

  init() {
    this.bindEvents();
    this.syncBottomNavHeight();
    this.startSessionPulse();
  }

  bindEvents() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.attachClickHandlers();
        this.attachPanelHandlers();
      });
    } else {
      this.attachClickHandlers();
      this.attachPanelHandlers();
    }
  }

  attachClickHandlers() {
    if (!this.widget) {
      console.error('WhatsApp Widget: No widget element available for event handlers');
      return;
    }
    
    try {
      // Remove any existing listeners to prevent duplicates
      this.widget.removeEventListener('click', this.handleClick.bind(this));
      this.widget.removeEventListener('keydown', this.handleKeydown.bind(this));
      
      // Add event listeners
      this.widget.addEventListener('click', this.handleClick.bind(this));
      this.widget.addEventListener('keydown', this.handleKeydown.bind(this));
    } catch (error) {
      console.error('WhatsApp Widget: Error attaching event handlers:', error);
    }
  }

  attachPanelHandlers() {
    if (!this.panel) return;

    if (this.panelClose) {
      this.panelClose.addEventListener('click', (event) => {
        event.preventDefault();
        this.hidePanel(true);
      });
    }

    if (this.panelButtons && this.panelButtons.length) {
      this.panelButtons.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.handleQuickMessageClick(btn);
        });
      });
    }
  }

  syncBottomNavHeight() {
    const updateHeightVar = () => {
      const nav = document.querySelector('.bottom-nav');
      if (!nav) {
        document.documentElement.style.setProperty('--bottom-nav-height', '0px');
        return;
      }

      const styles = window.getComputedStyle(nav);
      const isHidden = styles.display === 'none' || styles.visibility === 'hidden';
      const height = isHidden ? 0 : nav.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--bottom-nav-height', `${Math.max(0, Math.round(height))}px`);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateHeightVar);
    } else {
      updateHeightVar();
    }

    window.addEventListener('resize', updateHeightVar, { passive: true });

    if (window.ResizeObserver) {
      const nav = document.querySelector('.bottom-nav');
      if (nav) {
        const observer = new ResizeObserver(() => updateHeightVar());
        observer.observe(nav);
      }
    }
  }

  startSessionPulse() {
    if (!this.widget) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Forced by request: run animation even if user prefers reduced motion
    }

    const navEntry = performance.getEntriesByType('navigation')[0];
    const navType = navEntry ? navEntry.type : 'navigate';
    const shouldPulseOnLoad = navType === 'reload';

    if (shouldPulseOnLoad) {
      this.runPulseSequence();
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.wasHidden = true;
        return;
      }

      if (document.visibilityState === 'visible' && this.wasHidden) {
        this.wasHidden = false;
        this.runPulseSequence();
      }
    });
  }

  runPulseSequence() {
    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        this.widget.classList.add('whatsapp-widget--pulse');
        window.setTimeout(() => {
          this.widget.classList.remove('whatsapp-widget--pulse');
          this.showPanelAfterPulse();
        }, 3600);
      });
    }, 4000);

    window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        this.widget.classList.add('whatsapp-widget--pulse');
        window.setTimeout(() => {
          this.widget.classList.remove('whatsapp-widget--pulse');
          this.showPanelAfterPulse();
        }, 3600);
      });
    }, 4000);
  }

  showPanelAfterPulse() {
    if (!this.panel) return;
    if (sessionStorage.getItem('whatsappWidgetPanelDismissed') === 'true') return;
    if (sessionStorage.getItem('whatsappWidgetPanelAutoShown') === 'true') return;
    this.panel.classList.add('is-open');
    this.panel.setAttribute('aria-hidden', 'false');
    sessionStorage.setItem('whatsappWidgetPanelAutoShown', 'true');
    if (this.panelAutoCloseTimer) {
      window.clearTimeout(this.panelAutoCloseTimer);
    }
    this.panelAutoCloseTimer = window.setTimeout(() => {
      this.hidePanel(false);
    }, 6000);
  }

  hidePanel(setDismissed = false) {
    if (!this.panel) return;
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    if (this.panelAutoCloseTimer) {
      window.clearTimeout(this.panelAutoCloseTimer);
      this.panelAutoCloseTimer = null;
    }
    if (setDismissed) {
      sessionStorage.setItem('whatsappWidgetPanelDismissed', 'true');
      sessionStorage.setItem('whatsappWidgetPanelAutoShown', 'true');
    }
  }

  openPanelManual() {
    if (!this.panel) return false;
    this.panel.classList.add('is-open');
    this.panel.setAttribute('aria-hidden', 'false');
    return true;
  }

  handleQuickMessageClick(button) {
    if (!button) return;

    const currentUrl = window.location.href;
    const template = button.dataset.message || '';
    const message = template.replace('{URL}', currentUrl);
    const whatsappUrl = this.buildWhatsAppUrl(message);
    this.openWhatsApp(whatsappUrl);
    this.trackEvent();
  }

  handleKeydown(event) {
    // Handle Enter and Space keys for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick(event);
    }
  }

  handleClick(event) {
    event.preventDefault();
    this.openPanelManual();
  }

  createMessage(pageUrl) {
    // Sanitize page title to prevent XSS
    const pageTitle = this.sanitizeText(document.title || 'Page');
    
    // Create message with custom prefix if available
    let message = '';
    if (this.customMessage.trim()) {
      message = `${this.customMessage}\n\n`;
    }

    message += `שלום! אני מעוניין/ת במוצר הזה: ${pageTitle}\n\nקישור לעמוד: ${pageUrl}`;

    return message;
  }

  sanitizeText(text) {
    // Basic XSS prevention - remove potentially dangerous characters
    return text.replace(/[<>\"'&]/g, function(match) {
      const map = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
        '&': '&amp;'
      };
      return map[match];
    });
  }

  buildWhatsAppUrl(message) {
    // Phone number should already be in correct international format (no spaces, hyphens, etc.)
    // But clean it just in case
    const cleanPhone = this.phoneNumber.replace(/[\s\-\(\)+]/g, '');
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Build WhatsApp URL according to official documentation
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    
    return whatsappUrl;
  }

  openWhatsApp(url) {
    try {
      // Open in new tab/window with security measures
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      
      // Check if popup was blocked
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Fallback: try to navigate current window
        window.location.href = url;
      }
    } catch (error) {
      console.error('WhatsApp Widget: Failed to open WhatsApp', error);
      // Fallback: try direct navigation
      try {
        window.location.href = url;
      } catch (fallbackError) {
        console.error('WhatsApp Widget: All open methods failed', fallbackError);
      }
    }
  }

  trackEvent() {
    // Track with Google Analytics if available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'click', {
        event_category: 'WhatsApp Widget',
        event_label: 'WhatsApp Contact',
        value: 1
      });
    }
    
    // Track with Facebook Pixel if available
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Contact', {
        content_name: 'WhatsApp Widget'
      });
    }
    
    // Track with custom analytics if available
    if (typeof analytics !== 'undefined' && analytics.track) {
      analytics.track('WhatsApp Widget Clicked', {
        page_url: window.location.href,
        page_title: document.title
      });
    }
  }

  // Method to update phone number dynamically if needed
  updatePhoneNumber(newNumber) {
    this.phoneNumber = newNumber;
  }

  // Method to show/hide widget
  toggle(show = true) {
    const widget = document.querySelector('.whatsapp-widget');
    if (widget) {
      widget.style.display = show ? 'flex' : 'none';
    }
  }

  // Method to temporarily hide widget (useful for certain pages)
  hide() {
    this.toggle(false);
  }

  // Method to show widget
  show() {
    this.toggle(true);
  }
}

// Initialize WhatsApp Widget when DOM is ready
function initWhatsAppWidget() {
  const widget = document.querySelector('.whatsapp-widget');
  if (widget && !widget.whatsappWidgetInstance) {
    widget.whatsappWidgetInstance = new WhatsAppWidget(widget);
    window.whatsappWidget = widget.whatsappWidgetInstance;
  }
}

// Single DOM ready handler
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhatsAppWidget);
} else {
  initWhatsAppWidget();
}

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = WhatsAppWidget;
}